<template>
  <div class="bio-link-instagram" :style="{color: sectionTextColor}">
    <p v-if="item.description" class="bio-link-instagram-desc all-text" :style="{color: sectionTextColor}">{{ item.description }}</p>
    <div class="bio-link-instagram-preview">
      <template v-if="loading">
        <div class="bio-link-instagram-avatar-loader"/>
        <div class="bio-link-instagram-text-loader">
          <div class="bio-link-instagram-loader-line bio-link-instagram-loader-line-name"/>
          <div class="bio-link-instagram-loader-line bio-link-instagram-loader-line-stats"/>
        </div>
      </template>
      <template v-else>
        <v-avatar size="44" :color="avatarSrc ? undefined : '#E1306C'">
          <v-img v-if="avatarSrc" :src="avatarSrc" eager :transition="false" @error="handleAvatarError"/>
          <v-icon v-else color="#FFFFFF" size="22">mdi-instagram</v-icon>
        </v-avatar>
        <div class="bio-link-instagram-info">
          <b class="bio-link-instagram-name">{{ displayName }}</b>
          <span v-if="statsLabel" class="bio-link-instagram-stats">{{ statsLabel }}</span>
        </div>
      </template>
      <button type="button" class="bio-link-instagram-follow">
        <v-icon size="14" color="#FFFFFF" class="mr-1">mdi-instagram</v-icon>
        {{ $t('follow') }}
      </button>
    </div>
  </div>
</template>

<script>
import { apiV1OpenGraph } from "../../../service/resources/api-v1-open-graph"

export default {
  name: 'InstagramLinkCard',
  props: {
    item: { type: Object, required: true },
    sectionTextColor: { type: String, default: '#FFFFFF' },
    isImageBroken: { type: Function, required: true },
    markImageBroken: { type: Function, required: true },
  },
  data: () => ({
    loading: true,
    avatarBroken: false,
    // Best-effort live enrichment fetched from Instagram's own public
    // profile page (server-side, see api-v1-open-graph's instagramProfile).
    // Stays null on any failure/parse miss, so the template always has a
    // safe static fallback (the configured item.image + "@username") to
    // drop back to instead of an empty or broken-looking card.
    profile: null,
  }),
  computed: {
    username: function() {
      const url = this.item.onlineAddress && this.item.onlineAddress.url
      const match = url && url.match(/instagram\.com\/([^/?#]+)/i)
      return (match && match[1]) || this.item.title || null
    },
    usingLiveAvatar: function() {
      return !!(this.profile && this.profile.image)
    },
    avatarSrc: function() {
      if (this.usingLiveAvatar && !this.avatarBroken) return this.profile.image
      if (this.item.image && !this.item.image.deleted && this.item.image.src && !this.isImageBroken(this.item.image.src)) return this.item.image.src
      return null
    },
    displayName: function() {
      return (this.profile && this.profile.name) || `@${this.item.title}`
    },
    statsLabel: function() {
      if (!this.profile || !this.profile.followers) return null
      return `${this.profile.followers} ${this.$t('instagramFollowers')} · ${this.profile.following} ${this.$t('instagramFollowing')}`
    },
  },
  created: function() {
    this.loadProfile()
  },
  methods: {
    handleAvatarError: function() {
      if (this.usingLiveAvatar) {
        this.avatarBroken = true
      } else if (this.item.image && this.item.image.src) {
        this.markImageBroken(this.item.image.src)
      }
    },
    loadProfile: async function() {
      if (!this.username) {
        this.loading = false
        return
      }
      try {
        const response = (await apiV1OpenGraph.instagramProfile(this.username)).data
        const image = response['og:image'] || null
        const title = response['og:title'] || ''
        const description = response['og:description'] || ''

        const nameMatch = title.match(/^(.*?)\s*\(@/)
        const name = nameMatch ? nameMatch[1].trim() : null

        // Instagram's og:description isn't just a word-for-word translation
        // between locales - the sentence structure itself changes:
        // EN: "1,146 Followers, 1,782 Following, 205 Posts - ..."
        // PT: "1,146 seguidores, seguindo 1,782, 205 posts - ..." (the verb
        // comes before its number in Portuguese, not after), so this needs
        // two separate patterns rather than one shared word-swap regex.
        // Digit groups only, chained via a real thousands-separator (comma
        // /dot immediately followed by exactly 3 digits) - a bare
        // [\d.,]+ is greedy enough to also swallow the comma that
        // separates "seguindo 1,782" from the post count right after it
        // ("1,782, 205 posts"), producing "1,782," as the captured value.
        const numberToken = '(\\d{1,3}(?:[.,]\\d{3})*(?:[.,]\\d+)?\\s?(?:mil|k|m|b)?)'
        const statsMatch =
          description.match(new RegExp(`^${numberToken}\\s+Followers,\\s*${numberToken}\\s+Following`, 'i')) ||
          description.match(new RegExp(`^${numberToken}\\s+seguidores,\\s*seguindo\\s+${numberToken}`, 'i'))

        if (image || name || statsMatch) {
          this.profile = {
            image: image || null,
            name: name || null,
            followers: statsMatch ? statsMatch[1].trim() : null,
            following: statsMatch ? statsMatch[2].trim() : null,
          }
        }
      } catch (error) {
        // Best-effort enrichment - falls back to the static preview below.
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.bio-link-instagram {
  padding: 0.9rem 1rem;
}
.bio-link-instagram-desc {
  font-size: 0.85rem;
  margin: 0 0 0.75rem;
  text-align: center;
}
.bio-link-instagram-preview {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(255,255,255,0.12);
  border-radius: 0.85rem;
  padding: 0.55rem 0.7rem;
  min-height: 58px;
}
.bio-link-instagram-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.bio-link-instagram-name {
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bio-link-instagram-stats {
  font-size: 0.72rem;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bio-link-instagram-follow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-weight: 700;
  font-size: 0.78rem;
  color: #FFFFFF;
  background: linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #FCAF45 100%);
  cursor: pointer;
  flex-shrink: 0;
}
.bio-link-instagram-avatar-loader,
.bio-link-instagram-loader-line {
  /* currentColor (inherits sectionTextColor from .bio-link-instagram),
     not a fixed white - the preview sits on a light background in some
     bio themes, where a translucent white block is invisible. Opacity is
     animated below (bio-instagram-pulse), so none is set statically here. */
  background: currentColor;
  border-radius: 999px;
  animation: bio-instagram-pulse 1.1s ease-in-out infinite;
}
.bio-link-instagram-avatar-loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
}
.bio-link-instagram-text-loader {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.bio-link-instagram-loader-line-name {
  height: 10px;
  width: 55%;
}
.bio-link-instagram-loader-line-stats {
  height: 8px;
  width: 75%;
}
@keyframes bio-instagram-pulse {
  0%, 100% { opacity: 0.12; }
  50% { opacity: 0.3; }
}
@media (prefers-reduced-motion: reduce) {
  .bio-link-instagram-avatar-loader,
  .bio-link-instagram-loader-line {
    animation: none;
  }
}
</style>
